import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Loader2, Network, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type Team } from '@/api/resourceCenterApi'
import { useAssignmentGroups, useInvalidateResourceProfiles } from '@/hooks/useResourceCenter'

/**
 * New team (z8nrz7f7dk): its name, an optional description and the groups in
 * it, chosen in one step. A group may already be in other teams — it stays
 * there too.
 */
export function CreateTeamDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (team: Team) => void
}) {
  const groups = useAssignmentGroups()
  const invalidate = useInvalidateResourceProfiles()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setDescription('')
    setPicked([])
    setSearch('')
    setError(null)
  }

  const create = useMutation({
    mutationFn: () =>
      resourceProfilesApi.createTeam({
        name: name.trim(),
        description: description.trim() || undefined,
        groupIds: picked,
      }),
    onSuccess: team => {
      void invalidate()
      reset()
      onCreated(team)
      onOpenChange(false)
    },
    onError: e => setError(e instanceof Error ? e.message : 'Something went wrong.'),
  })

  const list = groups.data ?? []
  const needle = search.trim().toLowerCase()
  const shown = needle ? list.filter(g => g.name.toLowerCase().includes(needle)) : list
  const people = new Set(list.filter(g => picked.includes(g.id)).flatMap(g => g.members.map(m => m.userId))).size

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-xl'>
        <DialogHeader className='space-y-1 border-b px-6 pb-4 pt-6 text-left'>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700'>
              <Network className='h-4 w-4' />
            </span>
            New team
          </DialogTitle>
          <DialogDescription>
            A team gathers groups — Micro holds Micro-1, Micro-2 and Micro QC. Everyone in its groups is in the team.
          </DialogDescription>
        </DialogHeader>

        <form
          className='space-y-4 px-6 py-5'
          onSubmit={e => {
            e.preventDefault()
            if (name.trim()) create.mutate()
          }}
          id='create-team'
        >
          {error && <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}

          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='team-name'>Name</Label>
              <Input
                id='team-name'
                autoFocus
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='e.g. Micro'
                maxLength={120}
                className='h-10'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='team-description'>
                Description <span className='font-normal text-muted-foreground'>(optional)</span>
              </Label>
              <Input
                id='team-description'
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder='What the team does'
                maxLength={500}
                className='h-10'
              />
            </div>
          </div>

          <div className='space-y-2'>
            <div className='flex items-center justify-between gap-2'>
              <Label>Groups in the team</Label>
              {list.length > 6 && (
                <div className='relative w-52'>
                  <Search className='pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-muted-foreground' />
                  <Input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder='Find a group'
                    aria-label='Find a group'
                    className='h-8 pl-8 text-sm'
                  />
                </div>
              )}
            </div>
            {groups.isLoading && <p className='py-4 text-center text-sm text-muted-foreground'>Loading groups…</p>}
            {groups.isSuccess && list.length === 0 && (
              <p className='rounded-xl border border-dashed px-3 py-6 text-center text-sm text-muted-foreground'>
                No groups yet — you can add them to the team later.
              </p>
            )}
            <ul className='grid max-h-[40vh] gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2'>
              {shown.map(g => {
                const on = picked.includes(g.id)
                return (
                  <li key={g.id}>
                    <button
                      type='button'
                      aria-pressed={on}
                      onClick={() => setPicked(p => (on ? p.filter(x => x !== g.id) : [...p, g.id]))}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all',
                        on ? 'border-indigo-400 bg-indigo-50/60 shadow-sm' : 'hover:border-foreground/20 hover:bg-muted/40',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                          on ? 'border-indigo-600 bg-indigo-600 text-white' : 'bg-background',
                        )}
                      >
                        {on && <Check className='h-3.5 w-3.5' />}
                      </span>
                      <span className='min-w-0'>
                        <span className='block truncate text-sm font-medium'>{g.name}</span>
                        <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                          <Users className='h-3 w-3' /> {g.members.length}
                          {g.teams.length > 0 && <span className='truncate'> · in {g.teams.map(t => t.name).join(', ')}</span>}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </form>

        <DialogFooter className='items-center gap-3 border-t bg-muted/30 px-6 py-3 sm:justify-between'>
          <p className='text-xs text-muted-foreground'>
            {picked.length} {picked.length === 1 ? 'group' : 'groups'} · {people} {people === 1 ? 'person' : 'people'}
          </p>
          <div className='flex gap-2'>
            <Button
              type='button'
              variant='ghost'
              onClick={() => {
                reset()
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>
            <Button type='submit' form='create-team' disabled={!name.trim() || create.isPending}>
              {create.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
              Create team
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
